import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast, ToastAction } from '@ui';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

const fetchLatestBuildId = async (): Promise<string | null> => {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!response.ok) return null;
    const data = await response.json();
    return typeof data?.buildId === 'string' ? data.buildId : null;
  } catch {
    return null;
  }
};

export const useVersionCheck = () => {
  const updateAvailable = useRef(false);
  const location = useLocation();
  const isFirstLocation = useRef(true);
  const enabled = import.meta.env.VITE_DEV_MODE === 'production';

  const notifyUpdate = useCallback(() => {
    if (updateAvailable.current) return;
    updateAvailable.current = true;
    toast({
      title: 'Nueva versión disponible',
      description: 'Recarga la página para usar la versión más reciente.',
      duration: Infinity,
      action: (
        <ToastAction altText="Recargar" onClick={() => window.location.reload()}>
          Recargar
        </ToastAction>
      ),
    });
  }, []);

  const check = useCallback(async () => {
    if (updateAvailable.current) return;
    const latest = await fetchLatestBuildId();
    if (latest && latest !== __BUILD_ID__) notifyUpdate();
  }, [notifyUpdate]);

  useEffect(() => {
    if (!enabled) return;

    check();
    const interval = window.setInterval(check, CHECK_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', check);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', check);
    };
  }, [enabled, check]);

  useEffect(() => {
    if (isFirstLocation.current) {
      isFirstLocation.current = false;
      return;
    }
    if (updateAvailable.current) window.location.reload();
  }, [location.pathname]);
};
